/**
 * Section Header Component
 */

import { ChevronDown, ChevronUp, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface SectionHeaderProps {
  title: string;
  icon: LucideIcon;
  /** The lane's true size, from the index/count query — never a loaded page's length. */
  totalCount: number;
  /** How many rows are actually loaded right now (0 while collapsed/disabled). */
  pageCount: number;
  headingId: string;
  controlsId: string;
  isCollapsed: boolean;
  onToggle: () => void;
}

export function SectionHeader({
  title,
  icon: Icon,
  totalCount,
  pageCount,
  headingId,
  controlsId,
  isCollapsed,
  onToggle,
}: SectionHeaderProps): JSX.Element {
  const contactWord = totalCount === 1 ? 'contact' : 'contacts';
  // While collapsed nothing has been fetched (pageCount is always 0), so
  // "0 of N contacts" would misleadingly read as an empty section — show
  // just the true total instead until it's expanded.
  const countLabel = isCollapsed
    ? `${totalCount} ${contactWord}`
    : `${pageCount} of ${totalCount} ${contactWord}`;

  return (
    <div className="mb-4 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <Icon className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
        <div>
          <h2
            id={headingId}
            className="text-lg font-semibold tracking-tight text-foreground"
          >
            {title}
          </h2>
          <p className="text-sm text-muted-foreground">
            {countLabel}
          </p>
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={onToggle}
        aria-expanded={!isCollapsed}
        aria-controls={controlsId}
        aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${title.toLowerCase()} section`}
        className="min-h-11 min-w-11 text-muted-foreground hover:text-foreground md:min-h-11 md:min-w-11"
      >
        {isCollapsed
          ? <ChevronDown className="h-5 w-5" aria-hidden="true" />
          : <ChevronUp className="h-5 w-5" aria-hidden="true" />}
      </Button>
    </div>
  );
}
